import type { ReactNode } from "react";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";

interface ConversationSystemMessageProps {
  id: string;
  content: string;
  testId?: string;
}

/** A centered, neutral pill between two hairlines. */
function ConversationSystemPill({
  dataTestId,
  role,
  children,
}: {
  dataTestId: string;
  role?: "status";
  children: ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-3 py-2"
      data-testid={dataTestId}
      role={role}
    >
      <span className="h-px flex-1 bg-glass-stroke-faint" />
      <span className="flex max-w-[74%] items-center gap-1.5 rounded-full border border-glass-stroke-faint bg-glass-fill px-3 py-1 text-center text-canvas-xs leading-snug text-muted">
        {children}
      </span>
      <span className="h-px flex-1 bg-glass-stroke-faint" />
    </div>
  );
}

export function ConversationSystemMessage({
  id,
  content,
  testId = "conversation-frame",
}: ConversationSystemMessageProps) {
  return (
    <ConversationSystemPill dataTestId={`${testId}-message-${id}`}>
      {content}
    </ConversationSystemPill>
  );
}

/** The neutral maintenance pill shown while the agent compacts its context. */
export function ConversationCompactingNote({
  testId = "conversation-frame",
}: {
  testId?: string;
}) {
  const t = useT(framesConversationMessages);
  return (
    <ConversationSystemPill dataTestId={`${testId}-compacting`} role="status">
      <Icons.Spinner
        className="h-3 w-3 shrink-0 animate-spin"
        aria-hidden="true"
      />
      <span>{t("compactingContext")}</span>
    </ConversationSystemPill>
  );
}

/** The status line above the composer while a submitted message is sent. */
export function ConversationFrameSubmitPending() {
  const t = useT(framesConversationMessages);
  return (
    <div
      role="status"
      data-testid="conversation-frame-submit-pending"
      className="px-3 py-1 text-canvas-xs text-muted"
    >
      {t("sending")}
    </div>
  );
}
